import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import logoAquaphys from "@/assets/logo-aquaphys.webp";

interface NavbarProps {
  onDemoClick: () => void;
}

const languages = [
  { code: "fr", flag: "🇫🇷" },
  { code: "en", flag: "🇬🇧" },
  { code: "de", flag: "🇩🇪" },
  { code: "es", flag: "🇪🇸" },
  { code: "it", flag: "🇮🇹" },
];

const Navbar = ({ onDemoClick }: NavbarProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 20);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const close = () => setLangOpen(false);
    if (langOpen) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [langOpen]);

  const links = [
    { label: t("nav.solution"), href: "#solution" },
    { label: t("nav.target"), href: "#pour-qui" },
    { label: t("nav.benefits"), href: "#metrics" },
    { label: t("nav.clients"), href: "#social-proof" },
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.blog"), href: "/blog", isRoute: true },
  ];

  const scrollTo = (link: { href: string; isRoute?: boolean }) => {
    setMobileOpen(false);
    if (link.isRoute) {
      navigate(link.href);
    } else if (window.location.pathname !== "/") {
      navigate("/" + link.href);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-primary/10 ${
        scrolled ? "bg-background/40 backdrop-blur-md shadow-md" : "bg-background/20 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <button
          onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center"
        >
          <img src={logoAquaphys} alt="Aquaphys" className="h-14 w-auto" width="380" height="80" fetchPriority="high" />
        </button>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-body"
            >
              {l.label}
            </button>
          ))}

          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-body px-2 py-1 rounded-md hover:bg-secondary/50"
            >
              <span className="text-base">{currentLang.flag}</span>
              <Globe size={14} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-card border border-primary/20 rounded-lg shadow-xl py-1 min-w-[140px] z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm font-body flex items-center gap-3 transition-colors ${
                      i18n.language === lang.code
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    {t(`lang.${lang.code}`)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={onDemoClick}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-body font-medium hover:glow-cyan transition-all"
          >
            {t("nav.demo")}
          </button>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-lg border-t border-primary/20 px-4 pb-4">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l)}
              className="block w-full text-left py-3 text-muted-foreground hover:text-foreground transition-colors font-body"
            >
              {l.label}
            </button>
          ))}
          <div className="flex gap-2 py-3">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`text-lg px-2 py-1 rounded ${i18n.language === lang.code ? "bg-primary/20" : "hover:bg-secondary/50"}`}
              >
                {lang.flag}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setMobileOpen(false); onDemoClick(); }}
            className="mt-2 w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-body font-medium"
          >
            {t("nav.demo")}
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
